import { on, reducer } from 'ts-action';
import * as ecgActions from '../../ecg/actions/ecg.actions';
import * as undoActions from '../../undo/actions/undo.actions';
import * as ruler2dActions from '../actions/ruler2d.actions';
import { UpdateMode } from '../../wave/utils/update.utils';

declare module 'react-redux' {
  interface DefaultRootState {
    ruler2d: Ruler2dState;
  }
}

export type XUpdatedKeys = keyof Pick<
  DefaultRuler2dState,
  'timestampStart' | 'timestampEnd'
>;

export type YUpdatedKeys = keyof Pick<
  DefaultRuler2dState,
  'voltageStart' | 'voltageEnd'
>;

export type UpdatedKeys = XUpdatedKeys | YUpdatedKeys;

export interface BaseRuler2dState {
  timestampStart: number;
  timestampEnd: number;
  voltageStart: number;
  voltageEnd: number;
  updateMode: null | UpdateMode;
}

export interface DefaultRuler2dState extends BaseRuler2dState {
  updateMode: null;
}

export interface BoundRuler2dState extends BaseRuler2dState {
  updateMode: 'boundary';
  xBoundary?: UpdatedKeys;
  yBoundary?: UpdatedKeys;
}

export interface MoveRuler2dState extends BaseRuler2dState {
  updateMode: 'move';
  previousTimestamp: number;
  previousVoltage: number;
}

export type Ruler2dState =
  | DefaultRuler2dState
  | BoundRuler2dState
  | MoveRuler2dState;

const initialStateFactory = (): Ruler2dState => ({
  timestampStart: 0,
  timestampEnd: 0,
  voltageStart: 0,
  voltageEnd: 0,
  updateMode: null,
});

export default reducer(
  initialStateFactory(),
  on(ecgActions.clean, () => initialStateFactory()),
  on(
    ruler2dActions.stop,
    ({
      timestampStart,
      timestampEnd,
      voltageStart,
      voltageEnd,
    }): Ruler2dState => ({
      timestampStart,
      timestampEnd,
      voltageStart,
      voltageEnd,
      updateMode: null,
    }),
  ),
  on(
    ruler2dActions.setBoundary,
    (
      { timestampStart, timestampEnd, voltageStart, voltageEnd, ...rest },
      { xBoundary, yBoundary, timestamp, voltage },
    ): Ruler2dState => {
      const [sortedTimestampStart, sortedTimestampEnd] = [
        timestampStart,
        timestampEnd,
      ].sort((a, b) => a - b);
      const [sortedvoltageStart, sortedvoltageEnd] = [
        voltageStart,
        voltageEnd,
      ].sort((a, b) => a - b);

      return {
        ...rest,
        timestampStart: timestamp ?? sortedTimestampStart,
        timestampEnd: timestamp ?? sortedTimestampEnd,
        voltageStart: voltage ?? sortedvoltageStart,
        voltageEnd: voltage ?? sortedvoltageEnd,
        ...(xBoundary ? { xBoundary } : {}),
        ...(yBoundary ? { yBoundary } : {}),
        updateMode: 'boundary',
      };
    },
  ),
  on(
    ruler2dActions.setMoving,
    (state, { timestamp, voltage }): Ruler2dState => ({
      ...state,
      previousTimestamp: timestamp,
      previousVoltage: voltage,
      updateMode: 'move',
    }),
  ),
  on(ruler2dActions.setTimestamp, (state, { timestamp }): Ruler2dState => {
    switch (state.updateMode) {
      case 'boundary':
        return {
          ...state,
          [`${state.xBoundary}`]: timestamp,
        };
      case 'move': {
        const delta = state.previousTimestamp - timestamp;
        return {
          ...state,
          timestampStart: state.timestampStart - delta,
          timestampEnd: state.timestampEnd - delta,
          previousTimestamp: timestamp,
        };
      }
      default:
        return state;
    }
  }),
  on(ruler2dActions.setVoltage, (state, { voltage }): Ruler2dState => {
    switch (state.updateMode) {
      case 'boundary':
        return {
          ...state,
          [`${state.yBoundary}`]: voltage,
        };
      case 'move': {
        const delta = state.previousVoltage - voltage;
        return {
          ...state,
          voltageStart: state.voltageStart - delta,
          voltageEnd: state.voltageEnd - delta,
          previousVoltage: voltage,
        };
      }
      default:
        return state;
    }
  }),
  on(
    undoActions.undo,
    undoActions.restore,
    (state, { diff: { ruler2d } }): Ruler2dState => {
      if (!ruler2d) {
        return state;
      }
      return { ...state, ...ruler2d };
    },
  ),
);
